import { createStore } from 'vuex';
import { db, doc, getDoc, setDoc, onAuthStateChanged, auth, onSnapshot } from './firebaseConfig';

const getCollectionName = (name) => {
  const prefix = process.env.VUE_APP_FIRESTORE_PREFIX || '';
  return `${prefix}${name}`;
};

export default createStore({
  state: {
    user: null,
    role: null,
    organizations: [],
    salaryTargets: {},
    emailSubscriptions: null,
    showVat: false,  // Add this - user's personal VAT preference
  },
  mutations: {
    setUser(state, user) {
      state.user = user;
    },
    setRole(state, role) {
      state.role = role;
    },
    setOrganizations(state, organizations) {
      state.organizations = organizations;
    },
    setEmailSubscriptions(state, subscriptions) {
      state.emailSubscriptions = subscriptions;
    },
    SET_SALARY_TARGETS(state, targets) {
      state.salaryTargets = targets;
    },
    SET_ORGANIZATION_WEB_SETTINGS(state, { orgName, settings }) {
      const org = state.organizations.find(o => o.orgName === orgName);
      if (org) {
        org.organizationWebSettings = { ...org.organizationWebSettings, ...settings };
      }
    },
    UPDATE_SALARY_TARGET(state, { businessId, month, percentage }) {
      if (!state.salaryTargets[businessId]) {
        state.salaryTargets[businessId] = {};
      }
      state.salaryTargets[businessId][month] = percentage;
      
      state.organizations.forEach(org => {
        org.businesses.forEach(business => {
          if (business.name === businessId) {
            if (!business.data) business.data = {};
            if (!business.data.salaryTarget) business.data.salaryTarget = {};
            business.data.salaryTarget[month] = percentage;
          }
        });
      });
    },
    UPDATE_CLOSED_DAYS(state, { businessId, closedDays }) {
      state.organizations.forEach(org => {
        org.businesses.forEach(business => {
          if (business.name === businessId) {
            if (!business.data) business.data = {};
            business.data.closedDays = closedDays;
          }
        });
      });
    },
    UPDATE_TAKEAWAY_ORDER_TYPES(state, { businessId, orderTypes }) {
      state.organizations.forEach(org => {
        org.businesses.forEach(business => {
          if (business.name === businessId) {
            if (!business.data) business.data = {};
            business.data.takeawayOrderTypes = orderTypes;
          }
        });
      });
    },
    UPDATE_BUSYNESS_THRESHOLDS(state, { businessId, thresholds }) {
      state.organizations.forEach(org => {
        org.businesses.forEach(business => {
          if (business.name === businessId) {
            if (!business.data) business.data = {};
            business.data.busynessThresholds = thresholds;
          }
        });
      });
    },
    UPDATE_BUSINESS_DATA(state, { orgName, businessId, data }) {
      state.organizations.forEach(org => {
        if (org.orgName === orgName) {
          org.businesses.forEach(business => {
            if (business.name === businessId) {
              business.data = { ...business.data, ...data };
            }
          });
        }
      });
    },
    setShowVat(state, showVat) {
      state.showVat = showVat;
    }
  },
  actions: {
    checkUser({ commit }) {
      return new Promise((resolve) => {
        onAuthStateChanged(auth, async (user) => {
          commit('setUser', user);
          if (user) {
            try {
              const userRef = doc(db, 'users', user.uid);
              const userSnap = await getDoc(userRef);
  
              if (userSnap.exists()) {
                const userData = userSnap.data();
                commit('setRole', userData.role);
                commit('setShowVat', userData.showVat || false);  // Add this line

                const allEmailSubscriptions = {
                  weeklyReport: {},
                  monthlyReport: {}
                };
                if (userData.organizations) {
                  for (const orgName in userData.organizations) {
                    const orgData = userData.organizations[orgName];
                    if (orgData.emailSubscriptions) {
                      if (orgData.emailSubscriptions.weeklyReport) {
                        Object.assign(allEmailSubscriptions.weeklyReport, orgData.emailSubscriptions.weeklyReport);
                      }
                      if (orgData.emailSubscriptions.monthlyReport) {
                        Object.assign(allEmailSubscriptions.monthlyReport, orgData.emailSubscriptions.monthlyReport);
                      }
                    }
                  }
                }
                commit('setEmailSubscriptions', allEmailSubscriptions);
  
                const organizationsData = [];

                // Handle new organization structure
                if (userData.organizations) {
                  for (const [orgName, orgData] of Object.entries(userData.organizations)) {
                    try {
                      const orgRef = doc(db, getCollectionName('organizations'), orgName);
                      const orgSnap = await getDoc(orgRef);
  
                      if (orgSnap.exists()) {
                        const orgDocData = orgSnap.data();
                        const orgInfo = {
                          orgName,
                          businesses: [],
                          defaultColorScheme: orgDocData.defaultColorScheme,
                          organizationWebSettings: orgDocData.organizationWebSettings || { showVat: false }
                        };
  
                        // If user is owner, get all businesses from organization
                        if (userData.role === 'owner' && orgDocData.businesses) {
                          const businessEntries = Object.entries(orgDocData.businesses)
                            .sort(([nameA], [nameB]) => nameA.localeCompare(nameB));
                          
                          businessEntries.forEach(([businessName, businessData]) => {
                            orgInfo.businesses.push({
                              name: businessName,
                              data: {
                                ...businessData,
                                salaryTarget: businessData.salaryTarget || {}
                              },
                            });
                          });
                        } 
                        // For non-owners, only get assigned businesses
                        else if (orgData.businesses && orgDocData.businesses) {
                          const assignedBusinesses = [...orgData.businesses].sort((a, b) => a.localeCompare(b));
                          
                          for (const businessName of assignedBusinesses) {
                            const businessData = orgDocData.businesses[businessName];
                            if (businessData) {
                              orgInfo.businesses.push({
                                name: businessName,
                                data: {
                                  ...businessData,
                                  salaryTarget: businessData.salaryTarget || {}
                                },
                              });
                            }
                          }
                        }
  
                        organizationsData.push(orgInfo);
                      }
                    } catch (orgError) {
                      console.error(`Error fetching organization ${orgName}:`, orgError);
                    }
                  }
                }
  
                commit('setOrganizations', organizationsData);
              }
            } catch (error) {
              console.error('Error fetching user data:', error);
              commit('setRole', null);
              commit('setOrganizations', []);
            }
          } else {
            commit('setRole', null);
            commit('setOrganizations', []);
          }
          resolve();
        });
      });
    },
    async updateBulkEmailSubscriptions({ state, commit }, { reportType, subscribed }) {
      const user = state.user;
      if (!user) return;

      const userRef = doc(db, 'users', user.uid);
      
      try {
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = { ...userSnap.data() };
          if (!userData.organizations) userData.organizations = {};

          state.organizations.forEach(org => {
            org.businesses.forEach(business => {
              const orgId = org.orgName;
              const businessId = business.name;

              if (!userData.organizations[orgId]) userData.organizations[orgId] = {};
              if (!userData.organizations[orgId].emailSubscriptions) userData.organizations[orgId].emailSubscriptions = {};
              if (!userData.organizations[orgId].emailSubscriptions[reportType]) userData.organizations[orgId].emailSubscriptions[reportType] = {};
              
              userData.organizations[orgId].emailSubscriptions[reportType][businessId] = subscribed;
            });
          });

          await setDoc(userRef, { organizations: userData.organizations }, { merge: true });

          const newSubscriptions = JSON.parse(JSON.stringify(state.emailSubscriptions || { weeklyReport: {}, monthlyReport: {}}));
          if (!newSubscriptions[reportType]) {
            newSubscriptions[reportType] = {};
          }
          state.organizations.forEach(org => {
            org.businesses.forEach(business => {
              newSubscriptions[reportType][business.name] = subscribed;
            });
          });
          commit('setEmailSubscriptions', newSubscriptions);
        }
      } catch (error) {
        console.error("Error updating bulk email subscriptions:", error);
        throw error;
      }
    },
    async updateEmailSubscription({ state, commit }, { orgId, businessId, reportType, subscribed }) {
      const user = state.user;
      if (!user) return;

      const userRef = doc(db, 'users', user.uid);
      
      try {
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
              const userData = { ...userSnap.data() };

              // Ensure path exists
              if (!userData.organizations) userData.organizations = {};
              if (!userData.organizations[orgId]) userData.organizations[orgId] = {};
              if (!userData.organizations[orgId].emailSubscriptions) userData.organizations[orgId].emailSubscriptions = {};
              if (!userData.organizations[orgId].emailSubscriptions[reportType]) userData.organizations[orgId].emailSubscriptions[reportType] = {};
              
              userData.organizations[orgId].emailSubscriptions[reportType][businessId] = subscribed;

              await setDoc(userRef, { organizations: userData.organizations }, { merge: true });

              // Manually update local state after successful firestore update.
              const newSubscriptions = JSON.parse(JSON.stringify(state.emailSubscriptions || { weeklyReport: {}, monthlyReport: {}}));
              if (!newSubscriptions[reportType]) {
                  newSubscriptions[reportType] = {};
              }
              newSubscriptions[reportType][businessId] = subscribed;
              commit('setEmailSubscriptions', newSubscriptions);
          }
      } catch (error) {
          console.error("Error updating email subscription:", error);
          throw error;
      }
    },
    async fetchSalaryTargets({ commit }) {
      try {
        const salaryTargetsRef = doc(db, 'salaryTargets', 'targets');
        const snapshot = await getDoc(salaryTargetsRef);
        if (snapshot.exists()) {
          commit('SET_SALARY_TARGETS', snapshot.data());
        } else {
          commit('SET_SALARY_TARGETS', {});
        }
      } catch (error) {
        console.error('Error fetching salary targets:', error);
      }
    },
    async updateSalaryTarget({ commit, state }, { businessId, month, percentage }) {
      try {
        // Find the organization that contains this business
        let targetOrgId;
        state.organizations.forEach(org => {
          org.businesses.forEach(business => {
            if (business.name === businessId) {
              targetOrgId = org.orgName;
            }
          });
        });

        if (!targetOrgId) {
          throw new Error('Organization not found for business');
        }

        const orgRef = doc(db, getCollectionName('organizations'), targetOrgId);
        const orgSnap = await getDoc(orgRef);
        
        if (orgSnap.exists()) {
          const orgData = orgSnap.data();
          
          // Initialize or update the salary target
          if (!orgData.businesses[businessId].salaryTarget) {
            orgData.businesses[businessId].salaryTarget = {};
          }
          
          orgData.businesses[businessId].salaryTarget[month] = percentage;
          
          // Update Firestore
          await setDoc(orgRef, orgData);
          
          // Update local state
          commit('UPDATE_SALARY_TARGET', { businessId, month, percentage });
        }
      } catch (error) {
        console.error('Error updating salary target:', error);
      }
    },
    async updateClosedDays({ commit, state }, { businessId, closedDays }) {
      try {
        let targetOrgId;
        state.organizations.forEach(org => {
          org.businesses.forEach(business => {
            if (business.name === businessId) {
              targetOrgId = org.orgName;
            }
          });
        });

        if (!targetOrgId) {
          throw new Error('Organization not found for business');
        }

        const orgRef = doc(db, getCollectionName('organizations'), targetOrgId);
        const orgSnap = await getDoc(orgRef);
        
        if (orgSnap.exists()) {
          const orgData = orgSnap.data();
          
          // Initialize if needed
          if (!orgData.businesses[businessId].closedDays) {
            orgData.businesses[businessId].closedDays = {
              commonDates: [],
              weeklyClosures: [],
              specificDates: []
            };
          }
          
          // Update with new structure
          orgData.businesses[businessId].closedDays = closedDays;
          
          // Update Firestore
          await setDoc(orgRef, orgData);
          
          // Update local state
          commit('UPDATE_CLOSED_DAYS', { businessId, closedDays });
        }
      } catch (error) {
        console.error('Error updating closed days:', error);
      }
    },
    async updateBusynessThresholds({ commit, state }, { businessId, thresholds }) {
      try {
        let targetOrgId;
        state.organizations.forEach(org => {
          org.businesses.forEach(business => {
            if (business.name === businessId) {
              targetOrgId = org.orgName;
            }
          });
        });

        if (!targetOrgId) {
          throw new Error('Organization not found for business');
        }

        const orgRef = doc(db, getCollectionName('organizations'), targetOrgId);
        const orgSnap = await getDoc(orgRef);

        if (orgSnap.exists()) {
          const orgData = orgSnap.data();
          orgData.businesses[businessId].busynessThresholds = thresholds;
          await setDoc(orgRef, orgData);
          commit('UPDATE_BUSYNESS_THRESHOLDS', { businessId, thresholds });
        }
      } catch (error) {
        console.error('Error updating busyness thresholds:', error);
      }
    },
    async updateTakeawayOrderTypes({ commit, state }, { businessId, orderTypes }) {
      try {
        let targetOrgId;
        state.organizations.forEach(org => {
          org.businesses.forEach(business => {
            if (business.name === businessId) {
              targetOrgId = org.orgName;
            }
          });
        });

        if (!targetOrgId) {
          throw new Error('Organization not found for business');
        }

        const orgRef = doc(db, getCollectionName('organizations'), targetOrgId);
        const orgSnap = await getDoc(orgRef);

        if (orgSnap.exists()) {
          const orgData = orgSnap.data();
          orgData.businesses[businessId].takeawayOrderTypes = orderTypes;
          await setDoc(orgRef, orgData);
          commit('UPDATE_TAKEAWAY_ORDER_TYPES', { businessId, orderTypes });
        }
      } catch (error) {
        console.error('Error updating takeaway order types:', error);
      }
    },
    setupRealTimeListeners({ commit, state }) {
      const unsubscribers = [];
      
      // Set up listeners for each organization
      state.organizations.forEach(org => {
        const orgRef = doc(db, getCollectionName('organizations'), org.orgName);
        const unsubscribe = onSnapshot(orgRef, (snapshot) => {
          if (snapshot.exists()) {
            const orgData = snapshot.data();
            
            // Update organization web settings
            if (orgData.organizationWebSettings) {
              commit('SET_ORGANIZATION_WEB_SETTINGS', {
                orgName: org.orgName,
                settings: orgData.organizationWebSettings
              });
            }
            
            // Update each business in this organization
            Object.entries(orgData.businesses || {}).forEach(([businessId, businessData]) => {
              commit('UPDATE_BUSINESS_DATA', {
                orgName: org.orgName,
                businessId,
                data: businessData
              });
            });
          }
        });
        
        unsubscribers.push(unsubscribe);
      });
      
      // Return function to unsubscribe all listeners
      return () => unsubscribers.forEach(unsubscribe => unsubscribe());
    },
    async toggleVAT({ commit, state }) {
      try {
        const user = state.user;
        if (!user) return;
        
        const newShowVat = !state.showVat;
        
        const userRef = doc(db, 'users', user.uid);
        await setDoc(userRef, { showVat: newShowVat }, { merge: true });
        
        commit('setShowVat', newShowVat);
      } catch (error) {
        console.error('Error toggling VAT:', error);
      }
    }
  },
  getters: {
    getShowVAT: (state) => () => {
      return state.showVat;
    }
  }
});
